import {useEffect, useMemo, useState} from 'react';

import {useQuestionSampleSetDetail} from './useQuestionSampleSetDetail';
import {useGetAiSupportingDataQuery} from '~/store/rtkQuery';

// Score thresholds
const SCORE_THRESHOLDS = {
  PERFECT: 1,
  HIGH: 0.9,
  MEDIUM: 0.6,
};

export const QuestionSampleSetData = ({
  questionSampleSetId,
}: {
  questionSampleSetId: string;
}) => {
  const [activeFilter, setActiveFilter] = useState<
    'all' | 'perfect' | 'high' | 'medium' | 'low'
  >('all');

  const {
    isLoading,
    error: fetchError,
    data,
    sampleData,
    fetchData,
    fetchSampleData,
  } = useQuestionSampleSetDetail({id: questionSampleSetId});

  const {data: supportingData} = useGetAiSupportingDataQuery();

  // Add helper function to get category title
  const getCategoryTitle = (categoryId: string) => {
    return (
      supportingData?.categories.find(c => c.id === categoryId)?.title ??
      categoryId
    );
  };

  useEffect(() => {
    fetchData();
    fetchSampleData();
  }, [fetchData, fetchSampleData]);

  // Calculate category matching scores and buckets
  const sampleScores = useMemo(() => {
    const scores: Record<string, number> = {};
    sampleData?.forEach(q => {
      const matchingNeighbours = q.nearest_neighbours.filter(neighbour =>
        neighbour.question.categories.some((category: string) =>
          q.dato_record.categories.includes(category),
        ),
      );
      scores[q.id] = matchingNeighbours.length / q.nearest_neighbours.length;
    });
    return scores;
  }, [sampleData]);

  const scoresArr = useMemo(() => {
    return Object.values(sampleScores);
  }, [sampleScores]);

  console.log(sampleScores);

  const meanScore =
    scoresArr.reduce((acc, score) => acc + score, 0) /
    Object.keys(sampleScores).length;

  // Calculate median score
  const sortedScores = scoresArr.sort((a, b) => a - b);
  const medianScore =
    sortedScores.length % 2 === 0
      ? (sortedScores[sortedScores.length / 2 - 1] +
          sortedScores[sortedScores.length / 2]) /
        2
      : sortedScores[Math.floor(sortedScores.length / 2)];

  // Calculate score buckets
  const scoreBuckets = {
    perfect: scoresArr.filter(score => score === SCORE_THRESHOLDS.PERFECT)
      .length,
    high: scoresArr.filter(
      score =>
        score >= SCORE_THRESHOLDS.HIGH && score < SCORE_THRESHOLDS.PERFECT,
    ).length,
    medium: scoresArr.filter(
      score =>
        score >= SCORE_THRESHOLDS.MEDIUM && score < SCORE_THRESHOLDS.HIGH,
    ).length,
    low: scoresArr.filter(score => score < SCORE_THRESHOLDS.MEDIUM).length,
  };

  // Filter samples based on active filter
  const filteredSampleData = useMemo(() => {
    return (
      sampleData?.filter((q, index) => {
        if (activeFilter === 'all') return true;
        const score = sampleScores[q.id];
        switch (activeFilter) {
          case 'perfect':
            return score === SCORE_THRESHOLDS.PERFECT;
          case 'high':
            return (
              score >= SCORE_THRESHOLDS.HIGH && score < SCORE_THRESHOLDS.PERFECT
            );
          case 'medium':
            return (
              score >= SCORE_THRESHOLDS.MEDIUM && score < SCORE_THRESHOLDS.HIGH
            );
          case 'low':
            return score < SCORE_THRESHOLDS.MEDIUM;
          default:
            return true;
        }
      }) ?? []
    );
  }, [sampleData, activeFilter, sampleScores]);

  if (fetchError) {
    return (
      <div role="alert" className="alert alert-error my-4">
        <span>{fetchError.message}</span>
      </div>
    );
  }

  if (isLoading || !data || !sampleData) {
    return (
      <div className="flex flex-col items-center justify-center p-10">
        <span className="loading loading-dots loading-lg"></span>
        <p>Loading</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-3">Question Sample Set Data</h1>

      <div className="alert alert-info mb-3 py-2">
        <div>
          <h3 className="font-bold">Category Matching Scores</h3>
          <p>Mean: {(meanScore * 100).toFixed(1)}%</p>
          <p>Median: {(medianScore * 100).toFixed(1)}%</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setActiveFilter('all')}
          className={`btn ${activeFilter === 'all' ? 'btn-primary' : 'btn-ghost'}`}>
          All ({sampleData.length})
        </button>
        <button
          onClick={() => setActiveFilter('perfect')}
          className={`btn ${
            activeFilter === 'perfect' ? 'btn-success' : 'btn-ghost'
          }`}>
          100% ({scoreBuckets.perfect})
        </button>
        <button
          onClick={() => setActiveFilter('high')}
          className={`btn ${
            activeFilter === 'high' ? 'btn-warning' : 'btn-ghost'
          }`}>
          90-99% ({scoreBuckets.high})
        </button>
        <button
          onClick={() => setActiveFilter('medium')}
          className={`btn ${
            activeFilter === 'medium'
              ? 'btn-warning bg-orange-300'
              : 'btn-ghost'
          }`}>
          60-89% ({scoreBuckets.medium})
        </button>
        <button
          onClick={() => setActiveFilter('low')}
          className={`btn ${activeFilter === 'low' ? 'btn-error' : 'btn-ghost'}`}>
          0-59% ({scoreBuckets.low})
        </button>
      </div>
      <div className="flex flex-col gap-4">
        {filteredSampleData.map((q, index) => {
          const categoryMatchScore = sampleScores[q.id];
          return (
            <details key={q.id} className="card bg-base-200 shadow-xl">
              <summary className="card-body cursor-pointer py-3 px-4">
                <div className="flex justify-between items-center">
                  <h2 className="card-title text-base">Sample ID: {q.id}</h2>
                  <span
                    className={`badge ${
                      categoryMatchScore === SCORE_THRESHOLDS.PERFECT
                        ? 'badge-success'
                        : categoryMatchScore >= SCORE_THRESHOLDS.HIGH
                          ? 'badge-warning'
                          : categoryMatchScore >= SCORE_THRESHOLDS.MEDIUM
                            ? 'badge-warning bg-orange-300'
                            : 'badge-error'
                    }`}>
                    Category Match Score:{' '}
                    {(categoryMatchScore * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="divider my-1"></div>

                <div className="mb-2">
                  <p className="font-semibold mb-1">Question:</p>
                  <p className="bg-base-100 p-2 rounded-lg">
                    {q.dato_record.question}
                  </p>
                  <div className="mt-1">
                    {q.dato_record.categories.map((category: string) => (
                      <span key={category} className="badge mr-2">
                        {getCategoryTitle(category)}
                      </span>
                    ))}
                  </div>
                </div>
              </summary>

              <div className="card-body pt-0 px-4 pb-3">
                <div>
                  <p className="font-semibold mb-2">Nearest Neighbours:</p>
                  <div className="grid gap-2">
                    {q.nearest_neighbours.map(neighbour => (
                      <div
                        key={neighbour.id}
                        className="bg-base-100 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm opacity-70">
                            ID: {neighbour.id}
                          </span>
                          <span className="badge badge-secondary">
                            Similarity:{' '}
                            {(neighbour.similarity * 100).toFixed(1)}%
                          </span>
                        </div>
                        <p className="mb-1">{neighbour.question.question}</p>
                        <div>
                          {neighbour.question.categories.map(
                            (category: string) => (
                              <span
                                key={category}
                                className={`badge mr-2 ${
                                  q.dato_record.categories.includes(category)
                                    ? 'badge-success'
                                    : 'badge-error'
                                }`}>
                                {getCategoryTitle(category)}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
};
